// Agenda des prochains jours : démarrages, fins prévues et fins dépassées (règles de la maquette).

import { OPS_PRM } from './compute.js';
import type { OpsChantier } from './types.js';

export type AgendaKind = 'start' | 'end' | 'late';

export interface AgendaEvent {
  k: AgendaKind;
  /** Date de l'événement (AAAA-MM-JJ). */
  d: string;
  code: string;
  /** Montant mis en avant : CA (démarrage), reste à facturer (fin) ou reste à facturer / dépenser (retard). */
  v: number;
  vl: 'de CA' | 'à facturer' | 'à dépenser';
  /** Points d'attention sur l'événement. */
  w: string[];
}

export const AGENDA_LABEL: Record<AgendaKind, string> = {
  start: 'Démarrage',
  end: 'Fin prévue',
  late: 'Fin dépassée',
};

const addDays = (iso: string, n: number): string =>
  new Date(Date.parse(iso) + n * 86_400_000).toISOString().slice(0, 10);
const daysBetween = (a: string, b: string): number =>
  Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

/** Événements des `hz` prochains jours, plus les fins déjà dépassées. Un chantier ne produit qu'un événement. */
export function buildAgenda(rows: OpsChantier[], asOf: string, hz: number = OPS_PRM.hz): AgendaEvent[] {
  const horizon = addDays(asOf, hz);
  const out: AgendaEvent[] = [];
  for (const r of rows) {
    if (r.deb && r.deb > asOf && r.deb <= horizon) {
      const w: string[] = [];
      if (!r.cond) w.push('sans conducteur');
      if (r.mp < 15) w.push(`marge prévue ${String(r.mp).replace('.', ',')} %`);
      out.push({ k: 'start', d: r.deb, code: r.code, v: r.ca, vl: 'de CA', w });
    } else if (r.fin && r.fin >= asOf && r.fin <= horizon) {
      const w: string[] = [];
      if (r.cr < 50) w.push(`avancement ${r.cr} % : date peu crédible`);
      if (r.raf >= 500 && r.fa != null && r.fa < 80) w.push(`facturé seulement ${r.fa} %`);
      if (!r.cond) w.push('sans conducteur');
      out.push({ k: 'end', d: r.fin, code: r.code, v: r.raf, vl: 'à facturer', w });
    } else if (r.c.fd && r.fin) {
      const w = [`fin dépassée de ${daysBetween(r.fin, asOf)} j`];
      if (r.cr < 90) w.push(`avancement ${r.cr} %`);
      out.push({
        k: 'late',
        d: r.fin,
        code: r.code,
        v: r.raf >= 500 ? r.raf : r.RC,
        vl: r.raf >= 500 ? 'à facturer' : 'à dépenser',
        w,
      });
    }
  }
  return out;
}

export interface AgendaGroup {
  label: string;
  kind: 'late' | 'week';
  events: AgendaEvent[];
}

const MOIS_COURTS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];
export const monthShort = (ym: string): string => MOIS_COURTS[Number(ym.slice(5, 7)) - 1] ?? '';

const mondayOf = (iso: string): string => {
  const wd = (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
  return addDays(iso, -wd);
};

/** Regroupe : fins dépassées d'abord (les plus anciennes en tête), puis une section par semaine. */
export function groupAgenda(events: AgendaEvent[], asOf: string): AgendaGroup[] {
  const groups: AgendaGroup[] = [];
  const late = events.filter((e) => e.k === 'late').sort((a, b) => a.d.localeCompare(b.d));
  if (late.length) groups.push({ label: 'Fin prévue dépassée', kind: 'late', events: late });
  const week0 = mondayOf(asOf);
  const byWeek = new Map<string, AgendaEvent[]>();
  for (const e of events
    .filter((x) => x.k !== 'late')
    .sort((a, b) => a.d.localeCompare(b.d) || a.k.localeCompare(b.k))) {
    const w = mondayOf(e.d);
    byWeek.set(w, [...(byWeek.get(w) ?? []), e]);
  }
  for (const [w, list] of [...byWeek].sort(([a], [b]) => a.localeCompare(b))) {
    const label =
      w === week0
        ? 'Cette semaine'
        : w === addDays(week0, 7)
          ? 'Semaine prochaine'
          : `Semaine du ${Number(w.slice(8, 10))} ${monthShort(w.slice(0, 7))}`;
    groups.push({ label, kind: 'week', events: list });
  }
  return groups;
}
